'use client'
import { useState, useRef, useEffect, useCallback } from 'react'
import TestamonialCard from '@components/TestamonialCard'

function useResponsiveCols(desktop, tablet, mobile) {
  const getCols = useCallback(() => {
    if (typeof window === 'undefined') return desktop
    if (window.innerWidth < 640)  return mobile  ?? 1
    if (window.innerWidth < 1024) return tablet  ?? Math.min(desktop, 2)
    return desktop ?? 3
  }, [desktop, tablet, mobile])

  const [cols, setCols] = useState(getCols)

  useEffect(() => {
    const onResize = () => setCols(getCols())
    window.addEventListener('resize', onResize)
    onResize()
    return () => window.removeEventListener('resize', onResize)
  }, [getCols])

  return cols
}

export default function ReviewsSlider({ reviews, reviewCard, config, dictionary, lang }) {
  const cols = useResponsiveCols(
    config.cardsDesktop ?? 3,
    config.cardsTablet  ?? 2,
    config.cardsMobile  ?? 1,
  )

  const show       = reviews.slice(0, config.maxReviews ?? 6)
  const total      = show.length
  const maxIdx     = Math.max(0, total - cols)

  const [idx, setIdx]  = useState(0)
  const touchStart     = useRef(null)

  // Clamp idx when cols changes (e.g. resize makes fewer slots available)
  useEffect(() => { setIdx(i => Math.min(i, Math.max(0, total - cols))) }, [cols, total])

  const prev = () => setIdx(i => Math.max(0, i - 1))
  const next = () => setIdx(i => Math.min(maxIdx, i + 1))
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX }
  const onTouchEnd   = (e) => {
    if (touchStart.current === null) return
    const delta = touchStart.current - e.changedTouches[0].clientX
    if (Math.abs(delta) > 40) delta > 0 ? next() : prev()
    touchStart.current = null
  }

  if (!show.length) return null

  return (
    <div style={{ position: 'relative', paddingInline: '1.5rem' }}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Track */}
      <div style={{ overflow: 'hidden' }}>
        <div style={{
          display: 'flex',
          gap: '1.5rem',
          transition: 'transform 0.4s ease',
          transform: `translateX(calc(-${idx} * ((100% + 1.5rem) / ${cols})))`,
        }}>
          {show.map((review, i) => (
            <div key={review._id ?? i} style={{ flexShrink: 0, width: `calc((100% - ${cols - 1} * 1.5rem) / ${cols})` }}>
              <TestamonialCard testamonial={review} reviewCard={reviewCard} dictionary={dictionary} lang={lang} />
            </div>
          ))}
        </div>
      </div>
      {/* Arrows */}
      {total > cols && (<>
        <button onClick={prev} disabled={idx === 0} aria-label="Previous" style={{ position: 'absolute', left: -8, top: '50%', transform: 'translateY(-50%)', zIndex: 4, background: idx === 0 ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.35)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: idx === 0 ? 'default' : 'pointer', color: '#fff', backdropFilter: 'blur(4px)', padding: 0, transition: 'background 0.2s' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <button onClick={next} disabled={idx >= maxIdx} aria-label="Next" style={{ position: 'absolute', right: -8, top: '50%', transform: 'translateY(-50%)', zIndex: 4, background: idx >= maxIdx ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.35)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: idx >= maxIdx ? 'default' : 'pointer', color: '#fff', backdropFilter: 'blur(4px)', padding: 0, transition: 'background 0.2s' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </>)}
      {/* Dots */}
      {total > cols && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: '1.25rem' }}>
          {Array.from({ length: maxIdx + 1 }).map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} aria-label={`Go to slide ${i + 1}`}
              style={{ width: i === idx ? 20 : 8, height: 8, borderRadius: 4, border: 'none', padding: 0, cursor: 'pointer', background: i === idx ? 'var(--color-main)' : '#d1d5db', transition: 'all 0.2s ease' }} />
          ))}
        </div>
      )}
    </div>
  )
}